import React from 'react';
import clsx from 'clsx';
import { SVGIcon } from '../../SVGIcon';
import type { HSPFinancialSpecsProps } from './HSPFinancialSpecs.types';
import { CircularProgress } from '../RaportHSPEstimation';
import { SectionSubtitle } from '@/components';

/**
 * Komponent wyświetlający dane finansowe pojazdu HSP
 *
 * @example
 * ```tsx
 * <HSPFinancialSpecs
 *   data={financialData}
 *   onCheckDetails={() => console.log('Check details')}
 * />
 * ```
 */
export const HSPFinancialSpecs: React.FC<HSPFinancialSpecsProps> = ({
  data,
  className,
  onCheckDetails,
}) => {
  const {
    basePrice,
    standardPackets,
    optionalPackets,
    totalStandardEquipmentValue,
    optionalEquipmentPrice,
    totalAdditionalEquipmentValue,
    equipmentPercentage,
    hasOptionalEquipment,
  } = data;

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className={clsx('mt-20', className)}>
      {/* Nagłówek */}
      <SectionSubtitle title="Dane finansowe" />

      {/* Główna zawartość */}
      <div className="mt-11 pb-6 lg:px-6">
        {/* Górna sekcja - Cena bazowa */}
        <div className="mb-8">
          <div className="flex items-center gap-[18px]">
            <div className="flex h-[46px] w-[46px] items-center justify-center rounded-[10px] bg-primary p-1">
              <SVGIcon
                name="price-tag"
                fill="white"
                aria-label="Ikona ceny bazowej"
              />
            </div>
            <div className="flex flex-col gap-[10px]">
              <span className="text-base font-normal leading-[14px] text-grayText">
                Cena bazowa
              </span>
              <span className="text-[19px] font-extrabold leading-[20px] text-text-primary">
                {formatPrice(basePrice)} PLN
              </span>
            </div>
          </div>
        </div>

        {/* Dolna sekcja z tłem */}
        <div className="relative min-h-[190px] rounded-card border border-gray-300 bg-white">
          {/* Grid z trzema kolumnami */}
          <div className="relative grid grid-cols-1 gap-8 lg:grid-cols-1 xl:grid-cols-3 xl:gap-0">
            {/* Lewa kolumna - Pakiety wyposażenia */}
            <div className="xl:pr-8">
              <div className="flex items-center gap-[14px] px-6 pb-2 pt-6">
                <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-primary-100 p-1">
                  <SVGIcon
                    name="file-plus"
                    size={20}
                    fill="primary"
                    aria-label="Ikona pakietów wyposażenia"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-normal leading-[14px] text-grayText">
                    Pakiety wyposażenia
                  </span>
                  <span className="mt-[4px] text-base font-extrabold leading-[20px] text-text-primary">
                    {formatPrice(totalStandardEquipmentValue)} PLN
                  </span>
                </div>
              </div>

              {/* Strzałka i lista pakietów */}
              <div className="px-6 py-2">
                {standardPackets.length > 0 && (
                  <>
                    {standardPackets.map((pkg, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          className="flex-shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M8 16C9.05058 16 10.0909 15.7931 11.0615 15.391C12.0321 14.989 12.914 14.3997 13.6569 13.6569C14.3997 12.914 14.989 12.0321 15.391 11.0615C15.7931 10.0909 16 9.05058 16 8C16 6.94943 15.7931 5.90914 15.391 4.93853C14.989 3.96793 14.3997 3.08601 13.6569 2.34315C12.914 1.60028 12.0321 1.011 11.0615 0.608964C10.0909 0.206926 9.05058 -1.56548e-08 8 0C5.87827 3.16163e-08 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16ZM7.79378 11.2356L12.2382 5.90222L10.8729 4.76444L7.05067 9.35022L5.07289 7.37156L3.816 8.62844L6.48267 11.2951L7.17067 11.9831L7.79378 11.2356Z"
                            fill="#0064FF"
                          />
                        </svg>

                        <span className="text-[13px] font-normal text-grayText">
                          {pkg.name} ({pkg.code}):{' '}
                          <strong>
                            {formatPrice(pkg.currentMarketPrice || 0)} PLN
                          </strong>
                        </span>
                      </div>
                    ))}

                    {/* Link sprawdź szczegóły */}
                    <div className="mt-6">
                      <button
                        onClick={onCheckDetails}
                        className="flex flex-col items-start gap-[3px] border-b border-primary"
                        aria-label="Sprawdź szczegóły pakietów"
                      >
                        <span className="text-xs font-medium uppercase leading-[20px] tracking-[0.03em] text-primary">
                          SPRAWDŹ Szczegóły
                        </span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Środkowa kolumna - Wyposażenie opcjonalne */}
            <div className="relative border-gray-300 p-6 xl:border-l xl:border-r xl:px-8">
              <div className="mb-6 flex items-center gap-[14px]">
                <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-yellow p-1">
                  <SVGIcon
                    name="trophy"
                    size={22}
                    fill="orange"
                    aria-label="Ikona wyposażenia opcjonalnego"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-normal leading-[14px] text-grayText">
                    Wyposażenie opcjonalne
                  </span>
                  <span className="mt-[4px] text-base font-extrabold leading-[20px] text-text-primary">
                    {formatPrice(optionalEquipmentPrice)} PLN
                  </span>
                </div>
              </div>

              {optionalPackets.map((pkg, index) => (
                <div key={index} className="ml-8 flex items-center gap-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="flex-shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M8 16C9.05058 16 10.0909 15.7931 11.0615 15.391C12.0321 14.989 12.914 14.3997 13.6569 13.6569C14.3997 12.914 14.989 12.0321 15.391 11.0615C15.7931 10.0909 16 9.05058 16 8C16 6.94943 15.7931 5.90914 15.391 4.93853C14.989 3.96793 14.3997 3.08601 13.6569 2.34315C12.914 1.60028 12.0321 1.011 11.0615 0.608964C10.0909 0.206926 9.05058 -1.56548e-08 8 0C5.87827 3.16163e-08 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16ZM7.79378 11.2356L12.2382 5.90222L10.8729 4.76444L7.05067 9.35022L5.07289 7.37156L3.816 8.62844L6.48267 11.2951L7.17067 11.9831L7.79378 11.2356Z"
                      fill="#CA9A39"
                    />
                  </svg>

                  <span className="text-[13px] font-normal text-grayText">
                    {pkg.name} ({pkg.code}):{' '}
                    <strong>
                      {formatPrice(pkg.currentMarketPrice || 0)} PLN
                    </strong>
                  </span>
                </div>
              ))}

              {/* Strzałka i tekst */}
              <div className="space-y-4">
                {!hasOptionalEquipment && (
                  <div className="">
                    <span className="text-[13px] font-normal leading-[15px] text-grayText">
                      Brak wyposażenia opcjonalnego
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Prawa kolumna - Podsumowanie i wykres */}
            <div className="relative flex flex-row items-start gap-2 p-6 xl:gap-6 xl:pl-8">
              <div className="absolute left-1/2 top-[-24px] -translate-y-1/2 xl:left-[-14px] xl:top-1/2">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M0 14C0 6.26818 6.26818 0 14 0C21.7318 0 28 6.26818 28 14C28 21.7318 21.7318 28 14 28C6.26818 28 0 21.7318 0 14ZM7.63636 16.5455C7.63636 16.2079 7.77045 15.8842 8.00914 15.6455C8.24782 15.4068 8.57154 15.2727 8.90909 15.2727H19.0909C19.4285 15.2727 19.7522 15.4068 19.9909 15.6455C20.2295 15.8842 20.3636 16.2079 20.3636 16.5455C20.3636 16.883 20.2295 17.2067 19.9909 17.4454C19.7522 17.6841 19.4285 17.8182 19.0909 17.8182H8.90909C8.57154 17.8182 8.24782 17.6841 8.00914 17.4454C7.77045 17.2067 7.63636 16.883 7.63636 16.5455ZM8.90909 10.1818C8.57154 10.1818 8.24782 10.3159 8.00914 10.5546C7.77045 10.7933 7.63636 11.117 7.63636 11.4545C7.63636 11.7921 7.77045 12.1158 8.00914 12.3545C8.24782 12.5932 8.57154 12.7273 8.90909 12.7273H19.0909C19.4285 12.7273 19.7522 12.5932 19.9909 12.3545C20.2295 12.1158 20.3636 11.7921 20.3636 11.4545C20.3636 11.117 20.2295 10.7933 19.9909 10.5546C19.7522 10.3159 19.4285 10.1818 19.0909 10.1818H8.90909Z"
                    fill="#20262A"
                  />
                </svg>
              </div>
              {/* Łączna wartość */}
              <div className="mb-6">
                <div className="mb-2 text-sm font-normal leading-[18px] text-grayText">
                  Łączna wartość wyposażenia dodatkowego
                </div>
                <div className="text-base font-extrabold leading-[20px] text-text-primary">
                  {formatPrice(totalAdditionalEquipmentValue)} PLN
                </div>
              </div>

              <div className="relative">
                <CircularProgress
                  percentage={equipmentPercentage}
                  size={140}
                  strokeWidth={3}
                  color="#0064FF"
                  backgroundColor="#ECF1F9"
                />
                {/* Percentage in center */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="font-inter text-text-primary">
                    <span className="text-[26px] font-extrabold">
                      {equipmentPercentage}
                    </span>
                    <span className="text-base font-extrabold"> %</span>
                  </div>
                  <div className="mt-1 font-inter text-[13px] font-normal text-grayText">
                    ceny bazowej
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
